import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useEffect, useState } from 'react';
import { Col, Row, Spinner, Stack } from 'react-bootstrap';
import { To, useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../components/buttons/SimpleButton';
import Stepper from '../../../../components/stepper/Stepper';
import { useToast } from '../../../../context/ToastContext';
import { useGetCourseQuery } from '../../../../services/endpoints/schedule/course';
import {
  useCreateEnrollmentMutation,
  useGetEnrollmentQuery,
  useUpdateEnrollmentMutation
} from '../../../../services/endpoints/schedule/enrollment';
import { Enrollment } from '../../../../types/enrollment.type';
import { getErrorMessage, getObjectDifference } from '../../../../utils/utils';
import { FadeIn } from '../../../animations/FadeIn';
import { EnrollmentForm } from './EnrollmentForm';

const steps = [{ name: 'Details', form: EnrollmentForm }];

export interface FormValues {
  id?: number;
  course_id?: { value: number; label: string };
  quantity: number | undefined;
  tuition: number;
  number_of_classes: number;
  scholarship_id?: string;
  notes?: string;
  invoice_notes?: string;
}

type Overwrite<T, NewT> = Omit<T, keyof NewT> & NewT;

export type CreateFormValues = Overwrite<
  FormValues,
  {
    course_id: number;
  }
>;

const formatFormValue = (formValue?: FormValues | Enrollment): CreateFormValues => {
  return JSON.parse(JSON.stringify(formValue)) as CreateFormValues;
};

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
}

interface AddEditInvoiceItemProps {
  editing?: boolean;
}

export const AddEditEnrollment: React.FC<AddEditInvoiceItemProps> = ({ editing }) => {
  const params = useParams();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search); // TODO: send course id to pull course info
  const [currentStep, setCurrentStep] = useState(0);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [createEnrollment] = useCreateEnrollmentMutation();
  const [updateEnrollment] = useUpdateEnrollmentMutation();
  const { data, error, isLoading } = useGetEnrollmentQuery(Number(params.id!), {
    skip: !editing || !params.id
  });
  const {
    data: cource_data,
    error: cource_error,
    isLoading: cource_loading
  } = useGetCourseQuery(Number(params.parent_courceId!), { skip: !params.parent_courceId });

  const [initialValues, setInitialValues] = useState<FormValues>({
    quantity: undefined,
    tuition: 0,
    number_of_classes: 0,
    scholarship_id: ''
  });

  const validationSchemas = [
    Yup.object({
      course_id: Yup.object({
        value: Yup.number().required('Session selection is required'),
        label: Yup.string().optional()
      }).required(),
      quantity: Yup.number()
        .nullable()
        .positive()
        .min(1, 'Quantity should be higher than 0')
        .required('Quantity is required'),
      tuition: Yup.number()
        .positive()
        .min(0, 'Tuition should be higher or equal to 0')
        .required('Tuition is required'),
      number_of_classes: Yup.number()
        .positive()
        .min(1, 'Number of classes should be higher than 0')
        .required('Number of classes is required'),
      scholarship_id: Yup.string()
        .nullable()
        .transform((value) => (value === '' || value === '0' ? null : value))
        .optional(),
      notes: Yup.string().optional(),
      invoice_notes: Yup.string().optional()
    })
  ];

  useEffect(() => {
    if (cource_data && cource_data.course) {
      setInitialValues((prevInitialValues) => ({
        ...prevInitialValues,
        course_id: { value: cource_data.course.id, label: cource_data.course.title ?? '' },
        tuition: cource_data.course.price_per_class ?? 0,
        number_of_classes: cource_data.course?.lessons?.length ?? 0
      }));
    }
  }, [cource_data]);

  useEffect(() => {
    if (editing && error && !isLoading) {
      addToast('Error while loading enrollment', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, error, isLoading]);

  useEffect(() => {
    if (data && data.enrollment) {
      const { enrollment } = data;
      setInitialValues({
        id: enrollment.id,
        course_id: { value: enrollment.course_id, label: enrollment.session_title ?? '' },
        quantity: enrollment.quantity,
        tuition: enrollment.tuition,
        number_of_classes: enrollment.number_of_classes,
        scholarship_id: enrollment.scholarship_id != null ? String(enrollment.scholarship_id) : '',
        notes: enrollment.notes ?? '',
        invoice_notes: enrollment.invoice_notes ?? ''
      });
    }
  }, [data]);

  const handleAddInvoice = async (formValue: FormValues) => {
    try {
      if (!formValue.scholarship_id || formValue.scholarship_id === '0')
        formValue.scholarship_id = '';

      const submission = formatFormValue(formValue);
      submission.course_id = formValue.course_id?.value ?? 0;
      if (editing) {
        const formattedEnrollment = formatFormValue(data?.enrollment);
        const update = getObjectDifference(formattedEnrollment, submission);
        if (Object.keys(update).length > 0) {
          submission.id = data?.enrollment.id;
          const res = await updateEnrollment(submission).unwrap();
          if (res && res.enrollment) {
            addToast(`Enrollment updated succesfully`, 'success');
          } else {
            addToast(`No changes to enrollment`, 'info');
          }
          // navigate(`/admin/enrollments/${data?.enrollment?.id}`);
          navigate(-1 as To);
        }
      } else {
        const res = await createEnrollment(submission).unwrap();
        if (res && res.enrollment) {
          // navigate(`/admin/enrollments/${res.enrollment.id}`);
          navigate(-1 as To);
          addToast('Enrollment created succesfully', 'success');
        } else throw new Error('A problem happened while creating enrollment');
      }
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleNext = async (
    validateForm: () => Promise<FormikErrors<FormValues>>,
    setTouched: (touched: FormikTouched<FormValues>, shouldValidate?: boolean) => void,
    values: FormValues
  ) => {
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      setCurrentStep(currentStep + 1);
    } else {
      setTouched({
        quantity: true,
        tuition: true,
        number_of_classes: true,
        scholarship_id: true,
        notes: true,
        invoice_notes: true
      });
    }
  };

  if (isLoading) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  return (
    <FadeIn className="p-lg-4">
      <Stack
        gap={3}
        className="py-lg-0 px-4 py-2"
      >
        <div>
          <BackButton
            text={`Back to enrollment${editing ? ' details' : 's'}`}
            color="#2B4E64"
          />
          <h2
            className="d-none d-lg-block text-info fw-bold mb-0"
            style={{ fontSize: '2.25rem' }}
          >
            {editing ? 'Edit' : 'New'} Enrollment
          </h2>
          {steps.length > 1 && (
            <Stepper
              steps={steps}
              currentStep={currentStep}
              editing={editing === true}
              onStepClick={(step) => setCurrentStep(step)}
            />
          )}
        </div>

        <div
          className="p-lg-5 p-3"
          style={{
            boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
            border: '1px solid #EBEBEB',
            borderRadius: 10
          }}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchemas[currentStep]}
            onSubmit={handleAddInvoice}
            enableReinitialize
          >
            {({
              submitForm,
              isSubmitting,
              validateForm,
              touched,
              errors,
              setTouched,
              values,
              setFieldValue
            }: FormikProps<FormValues>) => (
              <FormikForm className="text-start">
                <div style={{ minHeight: '65vh' }}>
                  {currentStep < steps.length &&
                    React.createElement(steps[currentStep]?.form, {
                      errors,
                      touched,
                      values,
                      setFieldValue
                    })}
                </div>

                <Row className="justify-content-end mt-3">
                  <Col
                    xs={6}
                    lg={3}
                  >
                    {currentStep !== 0 && (
                      <SimpleButton
                        disabled={isSubmitting}
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="w-100"
                        variant="outline-primary"
                      >
                        Back
                      </SimpleButton>
                    )}
                  </Col>

                  <Col
                    xs={6}
                    lg={3}
                  >
                    <SimpleButton
                      disabled={isSubmitting}
                      type="button"
                      onClick={
                        currentStep === steps.length - 1
                          ? submitForm
                          : () => handleNext(validateForm, setTouched, values)
                      }
                      className="w-100"
                      variant="primary"
                    >
                      {currentStep === steps.length - 1 ? 'Finish' : 'Continue'}
                    </SimpleButton>
                  </Col>
                </Row>
              </FormikForm>
            )}
          </Formik>
        </div>
      </Stack>
    </FadeIn>
  );
};
